import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, Users2 } from 'lucide-react';
import { API } from '@/App';
import { HomeworkAttachment } from '@/components/HomeworkAttachment';

const when = (iso) => {
  if (!iso) return '';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  return new Date(iso).toLocaleDateString();
};

/**
 * What others shared on this worksheet — read beside the exercise, not in a pile at the end of
 * the section, and folded shut so the page stays a page.
 *
 * Anonymous: the server strips who wrote what before it ever reaches another member.
 */
export function WorksheetSharedFeed({ worksheetId }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    axios.get(`${API}/worksheets/${worksheetId}/shared`)
      .then((res) => setItems(res.data.submissions || []))
      .catch(() => setItems([]));
  }, [worksheetId]);

  if (!items.length) return null;

  return (
    <div className="rounded-2xl border border-gray-200 bg-gray-50/70 mb-4"
      data-testid={`worksheet-shared-${worksheetId}`}>
      <button type="button" onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-5 py-3.5 text-left"
        data-testid="worksheet-shared-toggle">
        <Users2 className="w-4 h-4 text-purple-600" />
        <span className="text-sm text-gray-800 font-medium flex-1">
          What others shared on this ({items.length})
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" />
              : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>

      {open && (
        <div className="px-5 pb-4 space-y-2">
          {items.map((item) => {
            const isOpen = !!expanded[item.id];
            const firstLine = (item.content || '')
              .replace(/\*/g, '').split('\n').filter(Boolean)[1] || '';
            return (
              <div key={item.id} className="rounded-xl bg-white border border-gray-200"
                data-testid={`shared-entry-${item.id}`}>
                <button type="button"
                  onClick={() => setExpanded((p) => ({ ...p, [item.id]: !isOpen }))}
                  className="w-full flex items-center gap-2 px-4 py-3 text-left"
                  data-testid={`shared-entry-toggle-${item.id}`}>
                  <span className="text-xs text-gray-500 shrink-0">
                    {item.is_mine ? 'Your submission' : (item.username || 'A member')}
                    {' · '}{when(item.created_at)}
                  </span>
                  <span className="text-xs text-gray-400 truncate flex-1">{firstLine}</span>
                  {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" />
                          : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
                </button>

                {isOpen && (
                  <div className="px-4 pb-4">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {(item.content || '').replace(/\*\*/g, '').replace(/^\*|\*$/gm, '')}
                    </p>
                    {item.file_url && (
                      <HomeworkAttachment url={item.file_url} name={item.file_name}
                                         testIdSuffix={`-${item.id}`} />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default WorksheetSharedFeed;
